// mixins.js

import Vue from "vue";
import { env } from "./env";
import { metaObj } from "../config/metadata/metaConfig";
import analytics from "./eventAnalytics/plugin";
import { getConfigurableRoutes } from "../router";

const mixins = {
	data() {
		return {
			env,
			routes: [],
			baseUrl: "",
			locale: "",
		};
	},
	created() {
	},
	metaInfo() {
		// I'm generating meta information for the current page
		if (!this.$route) return {};
		const currentCountry = env.country;
		const currentPage = this.$route.name;
		const pageMetaMap = {
			"spribe-game": "avaitorMeta",
			virtual: "virtualsMeta",
			virtuals: "virtualsMeta",
			casino: "casinoMeta",
			"betika-fasta": "betikaFastaMeta",
			promotions: "promotionsMeta",
			"crash-games": "crashGamesMeta",
		};
		const metaKey = pageMetaMap[currentPage];
		const metaConfig = metaObj[currentCountry][metaKey];

		let metaTags = {};
		if (metaConfig) {
			metaTags = {
				title: metaConfig.title,
				meta: [
					{
						vmid: "description",
						name: "description",
						content: metaConfig.description,
					},
				],
			};
		}

		// I'm adding hreflang links to the meta tags
		const links = this.hreflangLink();
		metaTags.link = links;

		return metaTags;
	},
	methods: {
		/**
		 * Tracks an analytics event using our centralized analytics system.
		 * @param {string} category - The event category
		 * @param {string|null} subcategoryOrEvent - The event subcategory or event name if no subcategory
		 * @param {string|Object} eventOrPayload - The event name or payload if no subcategory
		 * @param {Object|null} payloadOrNull - The payload or null if no subcategory
		 */

		
		analyticsTrack(
			category,
			subcategoryOrEvent,
			eventOrPayload,
			payloadOrNull
		) {
			if (payloadOrNull !== undefined) {
				// Subcategory is provided
				if (
					analytics[category] &&
					analytics[category][subcategoryOrEvent] &&
					analytics[category][subcategoryOrEvent][eventOrPayload]
				) {
					analytics[category][subcategoryOrEvent][eventOrPayload](
						payloadOrNull
					);
				} else {
					console.warn(
						`Analytics event not found: ${category}.${subcategoryOrEvent}.${eventOrPayload}`
					);
				}
			} else {
				// No subcategory
				if (analytics[category] && analytics[category][subcategoryOrEvent]) {
					analytics[category][subcategoryOrEvent](eventOrPayload);
				} else {
					console.warn(
						`Analytics event not found: ${category}.${subcategoryOrEvent}`
					);
				}
			}
		},
		productAvailable(product) {
			// I'm checking if a specific product is available
			return env.availableProducts.includes(product);
		},
		supportsV2Payments() {
			// I'm checking if V2 payments are supported
			return this.env.availableProducts.includes("paymentsV2");
		},
		hreflangLink() {
			// I'm generating hreflang links for SEO
			let vm = this;
			const links = vm.env.availableCountries
				.map((country) => {
					let path = vm.$route.path.replace(`/${vm.$route.params.locale}/`, "");
					let route = vm.$route.name;
					if (route == "boosted-multibet-events")
						path = `/${vm.env.sababishaProduct[country.code]}`;
					if (route == "boosted-multibet-event")
						path = `/${vm.env.sababishaProduct[country.code]}/${vm.$route.params.id}`;
					if (route == "shikisha-bet")
						path = `/${vm.env.shikishaProduct[country.code]}`;
					return country.availableLocales.map((locale) => {
						return {
							rel: "alternate",
							hreflang: locale,
							href: `${vm.env.desktopUrl}/${locale}${path}`.toLowerCase(),
						};
					});
				})
				.flat();
			links.push(
				{
					rel: "alternate",
					hreflang: "x-default",
					href: `${vm.env.desktopUrl}/${vm.env.locale}${vm.$route.path}`,
				},
				{
					rel: "canonical",
					href: `${vm.env.desktopUrl}/${vm.env.locale}${vm.$route.path}`.toLowerCase(),
				}
			);
			return links;
		},
		// Sitemap generation methods
		initBaseUrl() {
			// I'm setting up the base URL for the sitemap
			const isProd = process.env.NODE_ENV === "production";
			this.baseUrl = isProd ? env.websiteUrl : "http://localhost:8080";
			this.locale = process.env.DEFAULT_I18N_LOCALE || "en-ke";

			if (!this.baseUrl.endsWith("/")) {
				this.baseUrl += "/";
			}

			this.baseUrl += `${this.locale}/`;
		},
		generateSitemap() {
			// I'm initializing the routes and baseUrl when the component is created
			this.routes = getConfigurableRoutes();
			this.initBaseUrl();
			// I'm generating the full XML sitemap
			let sitemap = '<?xml version="1.0" encoding="UTF-8"?>\n';
			sitemap +=
				'<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';

			this.routes.forEach((route) => {
				if (this.shouldIncludeRoute(route)) {
					sitemap += this.generateUrlEntry(route);
				}
			});

			sitemap += "</urlset>";
			return sitemap;
		},
		shouldIncludeRoute(route) {
			// I'm determining if a route should be included in the sitemap
			const excludedPaths = [
				"*",
				"login",
				"register",
				"profile",
				"betslip",
				"mybets",
			];
			const excludedPrefixes = ["m/", "live/m/"];

			const path = route.path.startsWith("/")
				? route.path.slice(1)
				: route.path;

			return (
				!(route.meta && route.meta.requireAuth) &&
				!path.includes(":") &&
				!excludedPaths.includes(path) &&
				!excludedPrefixes.some((prefix) => path.startsWith(prefix))
			);
		},
		generateUrlEntry(route) {
			// I'm generating a single URL entry for the sitemap
			let entry = "  <url>\n";
			entry += `    <loc>${this.baseUrl}${this.getRouteUrl(route)}</loc>\n`;

			const lastMod = this.getLastMod(route);
			if (lastMod) {
				entry += `    <lastmod>${lastMod}</lastmod>\n`;
			}

			const changeFreq = this.getChangeFreq(route);
			if (changeFreq) {
				entry += `    <changefreq>${changeFreq}</changefreq>\n`;
			}

			const priority = this.getPriority(route);
			if (priority !== null) {
				entry += `    <priority>${priority.toFixed(1)}</priority>\n`;
			}

			entry += "  </url>\n";
			return entry;
		},
		getRouteUrl(route) {
			// I'm getting the URL for a route
			let path = route.path.startsWith("/") ? route.path.slice(1) : route.path;
			return path === "" ? "" : path;
		},
		getLastMod(route) {
			// I'm getting the last modification date for a route
			// For now, I'm just returning the current date
			return new Date().toISOString().split("T")[0];
		},
		getChangeFreq(route) {
			// I'm determining the change frequency for a route
			if (route.path === "" || route.path === "/") return "daily";
			if (route.path.startsWith("live")) return "always";
			if (route.path.startsWith("s/")) return "hourly";
			if (route.path.startsWith("jackpots")) return "daily";
			if (route.path.startsWith("virtuals")) return "hourly";
			if (route.path.startsWith("casino")) return "weekly";
			return "monthly";
		},
		getPriority(route) {
			// I'm determining the priority for a route
			if (route.path === "" || route.path === "/") return 1.0;
			if (route.path.startsWith("live")) return 0.9;
			if (route.path.startsWith("s/")) return 0.8;
			if (route.path.startsWith("jackpots")) return 0.7;
			if (route.path.startsWith("virtuals")) return 0.6;
			if (route.path.startsWith("casino")) return 0.5;
			return 0.4;
		},
	},
};

// I'm applying the mixins globally
Vue.mixin(mixins);

const RegisterMixins = () => {
	// I'm creating a function to register the mixins
	return new Promise((resolve, reject) => {
		Vue.mixin(mixins);
		resolve(true);
	});
};

export { RegisterMixins };
